/**
 * Wiex first/last-touch attribution for Shopify.
 *
 * Captures paid-click IDs and UTMs after Shopify marketing consent, stores them
 * first-party for 90 days, and copies them to Shopify cart attributes. Shopify
 * carries cart attributes over to the resulting order.
 */
(function (window, document) {
  'use strict';

  var VERSION = '1.0.0';
  var STORAGE_KEY = 'wx_attribution_v1';
  var COOKIE_NAME = 'wx_attribution_v1';
  var SYNC_KEY = 'wx_attribution_cart_hash_v1';
  var MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
  // Keep the redundant cookie comfortably below the browser's 4 KB limit.
  var MAX_VALUE_LENGTH = 120;
  var consentReady = false;
  var syncPromise = null;
  var bypassedForms = typeof WeakSet === 'function' ? new WeakSet() : null;

  var PARAMS = [
    'gclid',
    'gbraid',
    'wbraid',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content'
  ];

  var ATTRIBUTE_KEYS = [
    'wx_attribution_version',
    'wx_first_click_id_type',
    'wx_first_click_id',
    'wx_first_utm_source',
    'wx_first_utm_medium',
    'wx_first_utm_campaign',
    'wx_first_utm_term',
    'wx_first_utm_content',
    'wx_first_landing_page',
    'wx_first_captured_at',
    'wx_last_click_id_type',
    'wx_last_click_id',
    'wx_last_utm_source',
    'wx_last_utm_medium',
    'wx_last_utm_campaign',
    'wx_last_utm_term',
    'wx_last_utm_content',
    'wx_last_landing_page',
    'wx_last_captured_at'
  ];

  function clean(value, maxLength) {
    if (value === null || typeof value === 'undefined') return '';
    return String(value)
      .replace(/[\u0000-\u001F\u007F]/g, '')
      .trim()
      .slice(0, maxLength || MAX_VALUE_LENGTH);
  }

  function safeParse(value) {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  function readCookie() {
    var prefix = COOKIE_NAME + '=';
    var cookies = document.cookie ? document.cookie.split(';') : [];
    for (var index = 0; index < cookies.length; index += 1) {
      var cookie = cookies[index].trim();
      if (cookie.indexOf(prefix) === 0) {
        try {
          return safeParse(decodeURIComponent(cookie.slice(prefix.length)));
        } catch (error) {
          return null;
        }
      }
    }
    return null;
  }

  function readStored() {
    var record = null;
    try {
      record = safeParse(window.localStorage.getItem(STORAGE_KEY));
    } catch (error) {
      record = null;
    }
    if (!record) record = readCookie();
    if (!record || !record.expires_at || Date.now() >= record.expires_at) {
      clearBrowserStorage();
      return null;
    }
    return record;
  }

  function writeStored(record) {
    var serialized = JSON.stringify(record);
    try {
      window.localStorage.setItem(STORAGE_KEY, serialized);
    } catch (error) {
      // The cookie below is the fallback for restricted localStorage.
    }
    document.cookie =
      COOKIE_NAME +
      '=' +
      encodeURIComponent(serialized) +
      '; Max-Age=' +
      MAX_AGE_SECONDS +
      '; Path=/; SameSite=Lax; Secure';
  }

  function clearBrowserStorage() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      window.sessionStorage.removeItem(SYNC_KEY);
    } catch (error) {
      // Storage can be unavailable in strict browser modes.
    }
    document.cookie =
      COOKIE_NAME +
      '=; Max-Age=0; Path=/; SameSite=Lax; Secure';
  }

  function marketingAllowed() {
    return Boolean(
      window.Shopify &&
        window.Shopify.customerPrivacy &&
        typeof window.Shopify.customerPrivacy.marketingAllowed === 'function' &&
        window.Shopify.customerPrivacy.marketingAllowed()
    );
  }

  function getTouch() {
    var search = new URLSearchParams(window.location.search || '');
    var values = {};
    PARAMS.forEach(function (name) {
      values[name] = clean(search.get(name));
    });

    var hasClickId = Boolean(values.gclid || values.gbraid || values.wbraid);
    var hasUtm = Boolean(
      values.utm_source ||
        values.utm_medium ||
        values.utm_campaign ||
        values.utm_term ||
        values.utm_content
    );
    if (!hasClickId && !hasUtm) return null;

    var clickIdType = values.gclid
      ? 'gclid'
      : values.gbraid
        ? 'gbraid'
        : values.wbraid
          ? 'wbraid'
          : '';
    var clickId = clickIdType ? values[clickIdType] : '';

    if (hasClickId && !values.utm_source) values.utm_source = 'google';
    if (hasClickId && !values.utm_medium) values.utm_medium = 'cpc';

    return {
      click_id_type: clickIdType,
      click_id: clickId,
      utm_source: values.utm_source,
      utm_medium: values.utm_medium,
      utm_campaign: values.utm_campaign,
      utm_term: values.utm_term,
      utm_content: values.utm_content,
      landing_page: clean(window.location.pathname || '/', 300),
      captured_at: new Date().toISOString()
    };
  }

  function capture() {
    if (!marketingAllowed()) return readStored();
    var touch = getTouch();
    var record = readStored();
    if (!touch) return record;

    record = record || {
      version: VERSION,
      first: touch,
      last: touch
    };
    if (!record.first) record.first = touch;
    record.version = VERSION;
    record.last = touch;
    record.expires_at = Date.now() + MAX_AGE_SECONDS * 1000;
    writeStored(record);
    return record;
  }

  function toAttributes(record) {
    if (!record || !record.first || !record.last) return {};
    var attributes = { wx_attribution_version: clean(record.version, 20) };
    ['first', 'last'].forEach(function (position) {
      var touch = record[position] || {};
      [
        'click_id_type',
        'click_id',
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
        'landing_page',
        'captured_at'
      ].forEach(function (field) {
        attributes['wx_' + position + '_' + field] = clean(touch[field]);
      });
    });
    return attributes;
  }

  function emptyAttributes() {
    var attributes = {};
    ATTRIBUTE_KEYS.forEach(function (key) {
      attributes[key] = '';
    });
    return attributes;
  }

  function stableHash(attributes) {
    var hash = 2166136261;
    var value = JSON.stringify(attributes);
    for (var index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return String(hash >>> 0);
  }

  function postAttributes(attributes, force) {
    var hash = stableHash(attributes);
    try {
      if (!force && window.sessionStorage.getItem(SYNC_KEY) === hash) {
        return Promise.resolve(true);
      }
    } catch (error) {
      // Continue with the network request if sessionStorage is unavailable.
    }

    if (syncPromise) return syncPromise;
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    syncPromise = window
      .fetch(root + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ attributes: attributes })
      })
      .then(function (response) {
        if (!response.ok) throw new Error('Shopify cart update failed: ' + response.status);
        try {
          window.sessionStorage.setItem(SYNC_KEY, hash);
        } catch (error) {
          // Non-fatal.
        }
        return true;
      })
      .catch(function (error) {
        if (window.console && typeof window.console.warn === 'function') {
          window.console.warn('[Wiex attribution] Cart sync failed.', error);
        }
        return false;
      })
      .then(function (result) {
        syncPromise = null;
        return result;
      });
    return syncPromise;
  }

  function syncToCart(force) {
    if (!marketingAllowed()) return Promise.resolve(false);
    var record = capture();
    if (!record) return Promise.resolve(false);
    return postAttributes(toAttributes(record), Boolean(force));
  }

  function removeCartAttributes() {
    return postAttributes(emptyAttributes(), true);
  }

  function addHiddenAttributes(form) {
    if (!marketingAllowed()) return;
    var record = readStored();
    if (!record) return;
    var attributes = toAttributes(record);
    Object.keys(attributes).forEach(function (key) {
      var name = 'attributes[' + key + ']';
      var input = form.querySelector('input[data-wx-attribution="' + key + '"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.setAttribute('data-wx-attribution', key);
        form.appendChild(input);
      }
      input.value = attributes[key];
    });
  }

  function isCheckoutSubmission(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return false;
    var action = clean(form.getAttribute('action')).split('?')[0].replace(/\/+$/, '');
    var cartPath = clean(
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/') + 'cart'
    ).replace(/\/+$/, '');
    var submitter = event.submitter || document.activeElement;
    return (
      action === cartPath &&
      Boolean(
        (submitter && submitter.name === 'checkout') ||
          form.querySelector('[name="checkout"]')
      )
    );
  }

  function handleCheckout(event) {
    if (!isCheckoutSubmission(event) || !marketingAllowed()) return;
    var form = event.target;
    if (bypassedForms && bypassedForms.has(form)) {
      bypassedForms.delete(form);
      return;
    }

    addHiddenAttributes(form);
    event.preventDefault();
    var submitter = event.submitter || form.querySelector('[name="checkout"]');
    Promise.race([
      syncToCart(true),
      new Promise(function (resolve) {
        window.setTimeout(resolve, 1500);
      })
    ]).then(function () {
      if (bypassedForms) bypassedForms.add(form);
      if (typeof form.requestSubmit === 'function' && submitter) {
        form.requestSubmit(submitter);
      } else {
        var checkout = document.createElement('input');
        checkout.type = 'hidden';
        checkout.name = 'checkout';
        checkout.value = 'Checkout';
        form.appendChild(checkout);
        form.submit();
      }
    });
  }

  function handleConsent(event) {
    var allowed =
      event &&
      event.detail &&
      typeof event.detail.marketingAllowed === 'boolean'
        ? event.detail.marketingAllowed
        : marketingAllowed();
    if (allowed) {
      capture();
      syncToCart(true);
    } else {
      clearBrowserStorage();
      removeCartAttributes();
    }
  }

  function start() {
    if (consentReady) return;
    consentReady = true;
    document.addEventListener('visitorConsentCollected', handleConsent);
    document.addEventListener('submit', handleCheckout, true);

    if (marketingAllowed()) {
      capture();
      syncToCart(window.location.pathname.indexOf('/cart') !== -1);
    } else if (readStored()) {
      clearBrowserStorage();
      removeCartAttributes();
    }
  }

  function loadConsentApi() {
    window.Shopify = window.Shopify || {};
    if (window.Shopify.customerPrivacy) {
      start();
      return;
    }
    if (typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (error) {
          if (!error) start();
        }
      );
    }
  }

  window.WXAttribution = {
    version: VERSION,
    capture: capture,
    read: readStored,
    attributes: function () {
      return toAttributes(readStored());
    },
    sync: syncToCart,
    clear: function () {
      clearBrowserStorage();
      return removeCartAttributes();
    }
  };

  loadConsentApi();
})(window, document);
